import type { Permission } from '@/constants'

export interface DemoMenuItemDefinition {
  key: string
  icon: string
  titleKey: string
  descKey: string
  /** 表示・遷移を許可する権限。未指定なら誰でも表示。 */
  requiredPermission?: Permission
  /** 複数権限のいずれかを持っていれば表示（OR）。requiredPermission と併用可。 */
  requiredAnyPermission?: Permission[]
}

export interface DemoMenuGroupDefinition {
  id: string
  titleKey: string
  icon: string
  colorClass: string
  topMenuTarget: string
  showOnTopMenu: boolean
  items: DemoMenuItemDefinition[]
  /** グループ全体の表示に必要な権限（OR）。いずれかを持っていれば表示。 */
  requiredAnyPermission?: Permission[]
}

export const DEMO_MENU_GROUPS: DemoMenuGroupDefinition[] = [
  {
    id: 'data-input',
    titleKey: 'groups.dataImport.title',
    icon: '📥',
    colorClass: 'from-purple-100 to-purple-50',
    topMenuTarget: 'ocr-read-list',
    showOnTopMenu: true,
    requiredAnyPermission: ['HOME_DATA_IMPORT_MENU'],
    items: [
      {
        key: 'data-import',
        icon: '📥',
        titleKey: 'groups.dataImport.items.csvImport.title',
        descKey: 'groups.dataImport.items.csvImport.desc',
        requiredPermission: 'DATA_IMPORT_CSV',
      },
      {
        key: 'ocr-read-import',
        icon: '📄',
        titleKey: 'groups.dataImport.items.ocrRead.title',
        descKey: 'groups.dataImport.items.ocrRead.desc',
        requiredPermission: 'DATA_IMPORT_OCR',
      },
    ],
  },
  {
    id: 'recipient-mgmt',
    titleKey: 'groups.recipientMgmt.title',
    icon: '👤',
    colorClass: 'from-teal-100 to-teal-50',
    topMenuTarget: 'recipient-search',
    showOnTopMenu: true,
    requiredAnyPermission: ['RECIPIENT_SEARCH'],
    items: [
      {
        key: 'recipient-search',
        icon: '👤',
        titleKey: 'groups.recipientMgmt.items.recipientSearch.title',
        descKey: 'groups.recipientMgmt.items.recipientSearch.desc',
        requiredPermission: 'RECIPIENT_SEARCH',
      },
      {
        key: 'application-register',
        icon: '📝',
        titleKey: 'groups.recipientMgmt.items.applicationRegister.title',
        descKey: 'groups.recipientMgmt.items.applicationRegister.desc',
        requiredPermission: 'APPLICATION_CREATE_MANUAL',
      },
    ],
  },
  {
    id: 'report',
    titleKey: 'groups.report.title',
    icon: '📈',
    colorClass: 'from-red-100 to-red-50',
    topMenuTarget: 'submenu-report',
    showOnTopMenu: false,
    items: [
      {
        key: 'ocr-read-list',
        icon: '📑',
        titleKey: 'groups.report.items.importHistory.title',
        descKey: 'groups.report.items.importHistory.desc',
        requiredPermission: 'DATA_IMPORT_HISTORY_SEARCH',
      },
      {
        key: 'business-report',
        icon: '📈',
        titleKey: 'groups.report.items.businessReport.title',
        descKey: 'groups.report.items.businessReport.desc',
        requiredAnyPermission: [
          'HOME_DATA_IMPORT_MENU',
          'HOME_PAYER_MGMT_MENU',
          'HOME_SYSTEM_MGMT_MENU',
          'RECIPIENT_SEARCH',
          'APPLICATION_VIEW',
          'PAYMENT_LIST_SEARCH',
        ],
      },
    ],
  },
  {
    id: 'job',
    titleKey: 'groups.job.title',
    icon: '⚙️',
    colorClass: 'from-yellow-100 to-yellow-50',
    topMenuTarget: 'payment-job',
    showOnTopMenu: true,
    requiredAnyPermission: ['HOME_PAYER_MGMT_MENU'],
    items: [
      {
        key: 'payment-job',
        icon: '💰',
        titleKey: 'groups.job.items.paymentJob.title',
        descKey: 'groups.job.items.paymentJob.desc',
        requiredPermission: 'PAYMENT_LIST_SEARCH',
      },
    ],
  },
  {
    id: 'admin',
    titleKey: 'groups.admin.title',
    icon: '👥',
    colorClass: 'from-gray-100 to-gray-50',
    topMenuTarget: 'submenu-admin',
    showOnTopMenu: true,
    // システム管理メニュー全体の表示条件。下位のいずれかの機能権限を持つ
    // ロール（= SYSTEM_ADMIN または ADMIN）のみ表示し、その他のロールには
    // システム管理カードを完全に隠す。
    requiredAnyPermission: [
      'USER_MGMT',
      'OPERATION_LOG_VIEW',
      'MUNICIPALITY_MGMT',
      'RELEASE_HISTORY_VIEW',
      'OCR_SUCCESS_STATS_VIEW',
      'HOME_SYSTEM_MGMT_MENU',
    ],
    items: [
      {
        key: 'user-management',
        icon: '👥',
        titleKey: 'groups.admin.items.userManagement.title',
        descKey: 'groups.admin.items.userManagement.desc',
        requiredPermission: 'USER_MGMT',
      },
      {
        key: 'log-operation-history',
        icon: '📋',
        titleKey: 'groups.admin.items.logHistory.title',
        descKey: 'groups.admin.items.logHistory.desc',
        requiredPermission: 'OPERATION_LOG_VIEW',
      },
      {
        key: 'municipality-management',
        icon: '🏛️',
        titleKey: 'groups.admin.items.municipalityManagement.title',
        descKey: 'groups.admin.items.municipalityManagement.desc',
        requiredPermission: 'MUNICIPALITY_MGMT',
      },
      {
        key: 'release-history',
        icon: '📝',
        titleKey: 'groups.admin.items.releaseHistory.title',
        descKey: 'groups.admin.items.releaseHistory.desc',
        requiredPermission: 'RELEASE_HISTORY_VIEW',
      },
      {
        key: 'ocr-success-stats',
        icon: '📊',
        titleKey: 'groups.admin.items.ocrSuccessStats.title',
        descKey: 'groups.admin.items.ocrSuccessStats.desc',
        requiredPermission: 'OCR_SUCCESS_STATS_VIEW',
      },
      {
        key: 'announcement-management',
        icon: '📢',
        titleKey: 'groups.admin.items.announcementManagement.title',
        descKey: 'groups.admin.items.announcementManagement.desc',
        requiredPermission: 'HOME_SYSTEM_MGMT_MENU',
      },
      {
        key: 'office-review-status-management',
        icon: '📋',
        titleKey: 'groups.admin.items.officeReviewStatusManagement.title',
        descKey: 'groups.admin.items.officeReviewStatusManagement.desc',
        requiredPermission: 'USER_MGMT',
      },
      {
        key: 'notice-settings-management',
        icon: '📄',
        titleKey: 'groups.admin.items.noticeSettingsManagement.title',
        descKey: 'groups.admin.items.noticeSettingsManagement.desc',
        requiredPermission: 'USER_MGMT',
      },
      {
        key: 'transfer-dat-settings-management',
        icon: '🏦',
        titleKey: 'groups.admin.items.transferDatSettingsManagement.title',
        descKey: 'groups.admin.items.transferDatSettingsManagement.desc',
        requiredPermission: 'USER_MGMT',
      },
      {
        key: 'privacy-policy-settings-management',
        icon: '🔏',
        titleKey: 'groups.admin.items.privacyPolicySettingsManagement.title',
        descKey: 'groups.admin.items.privacyPolicySettingsManagement.desc',
        requiredPermission: 'USER_MGMT',
      },
      {
        key: 'recipient-csv-column-settings',
        icon: '🗂️',
        titleKey: 'groups.admin.items.recipientCsvColumnSettings.title',
        descKey: 'groups.admin.items.recipientCsvColumnSettings.desc',
        requiredPermission: 'USER_MGMT',
      },
    ],
  },
]

export const DEMO_HOME_ANNOUNCEMENTS = [
  {
    toneClass: 'text-amber-500',
    mark: '●',
    bodyClass: 'font-semibold text-amber-900',
    text: '支払日の8開庁日前です。',
  },
  {
    toneClass: 'text-gray-400',
    mark: '・',
    bodyClass: '',
    text: '来週の申出書提出期限は金曜17時までです。遅延時は窓口へご連絡ください。',
  },
  {
    toneClass: 'text-gray-400',
    mark: '・',
    bodyClass: '',
    text: '新しい様式の利用手順は、職員向けポータルの「資料ダウンロード」をご確認ください。',
  },
  {
    toneClass: 'text-gray-400',
    mark: '・',
    bodyClass: '',
    text: '問い合わせが集中している場合、折り返しまでにお時間をいただくことがあります。',
  },
]
